/**
 * @file Notification read-state callable helpers.
 */

import {
    markAllNotificationsReadCallable as configuredMarkAllNotificationsReadCallable,
    markNotificationAsReadCallable as configuredMarkNotificationAsReadCallable,
} from "../firebase/callables.js";
import { openConfirmationModal as configuredOpenConfirmationModal } from "../../shared/ui/confirmationModal.js";

/**
 * Creates notification API helpers.
 * @param {object} deps Dependency bag.
 * @returns {object} Notification API helpers.
 */
export function createNotificationApi({
    markAllNotificationsReadCallable,
    markNotificationAsReadCallable,
    openConfirmationModal,
}) {
/**
 * Marks all specified notifications as read by calling a secure cloud function.
 * @param {string[]} notificationIds - An array of notification IDs to mark as read.
 */
async function markAllNotificationsRead(notificationIds) {
    if (!notificationIds || notificationIds.length === 0) {
        console.warn("markAllNotificationsRead: No notification IDs provided.");
        return;
    }

    try {
        // Call the backend function we will build at the end of Stage 2
        await markAllNotificationsReadCallable({ ids: notificationIds });

        // No need to do anything else. The onSnapshot app data listener
        // will detect the changes and update the UI automatically.
        // console.log(`Successfully called markAllNotificationsRead for ${notificationIds.length} items.`); // Debug log - commented out
    } catch (error) {
        console.error(`Error calling markAllNotificationsReadCallable:`, error);
        const errorMessage = (error.code === 'internal' || !error.message)
            ? 'Something went wrong. Please try again later.'
            : `Failed to dismiss all notifications: ${error.message}`;
        openConfirmationModal('Error', errorMessage, async () => { }, false, 'OK');
    }
}

/**
 * Calls a secure cloud function to mark a single notification as read.
 * @param {string} notificationId - The ID of the notification to mark as read.
 * @param {object} [options] Call options.
 * @param {boolean} [options.throwOnError=false] Re-throw callable failures.
 */
async function markSingleNotificationRead(notificationId, options = {}) {
    if (!notificationId) {
        console.warn("markSingleNotificationRead: No notification ID provided.");
        return;
    }

    try {
        await markNotificationAsReadCallable({ notificationId: notificationId });
        // Success. The onSnapshot app data listener will handle the UI.
    } catch (error) {
        console.error(`Error calling markNotificationAsReadCallable:`, error);
        if (options.throwOnError) {
            throw error;
        }
        // Don't alert the user, just log the error.
    }
}


    return {
        markAllNotificationsRead,
        markSingleNotificationRead,
    };
}

const configuredNotificationApi = createNotificationApi({
    markAllNotificationsReadCallable: configuredMarkAllNotificationsReadCallable,
    markNotificationAsReadCallable: configuredMarkNotificationAsReadCallable,
    openConfirmationModal: configuredOpenConfirmationModal,
});

export const markAllNotificationsRead =
    configuredNotificationApi.markAllNotificationsRead;
export const markSingleNotificationRead =
    configuredNotificationApi.markSingleNotificationRead;
